import { useEffect, useRef, TextareaHTMLAttributes, forwardRef, useImperativeHandle } from 'react';

import { cn } from '@/helpers/shadCnUtils';

interface ResizingTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  value: string | undefined;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  className?: string;
}

interface ResizingTextareaRef {
  focus: () => void;
  setSelectionRange: (start: number, end: number) => void;
}

const ResizingTextarea = forwardRef<ResizingTextareaRef, ResizingTextareaProps>(
  ({ value, onChange, placeholder = '', minRows = 1, maxRows = 10, className = '', ...props }, ref) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => ({
      focus: () => {
        textareaRef.current?.focus();
      },
      setSelectionRange: (start: number, end: number) => {
        textareaRef.current?.setSelectionRange(start, end);
      },
    }));

    const adjustHeight = () => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      textarea.style.height = 'auto';

      const computedStyle = window.getComputedStyle(textarea);
      const lineHeight = parseInt(computedStyle.lineHeight) || parseInt(computedStyle.fontSize) * 1.5;

      const minHeight = lineHeight * minRows;
      const maxHeight = lineHeight * maxRows;

      // Calculate available space in the container
      const rect = textarea.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const availableHeight = viewportHeight - rect.top - 40; // 40px buffer for padding/margins

      // Use the smallest of: content height, max rows height, or available container height
      const constrainedMaxHeight = Math.min(maxHeight, availableHeight);
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), constrainedMaxHeight);

      textarea.style.height = `${newHeight}px`;
    };

    useEffect(() => {
      adjustHeight();
    }, [value, minRows, maxRows]);

    useEffect(() => {
      const handleResize = () => adjustHeight();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange) {
        onChange(e);
      }

      setTimeout(adjustHeight, 0);
    };

    return (
      <textarea
        ref={textareaRef}
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        className={cn(
          'border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring flex min-h-10 w-full resize-none rounded-md border px-3 py-2 text-base focus-visible:ring-2 focus-visible:ring-blue-200 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        style={{
          overflow: 'auto',
        }}
        {...props}
      />
    );
  },
);

ResizingTextarea.displayName = 'ResizingTextarea';

export default ResizingTextarea;
export type { ResizingTextareaRef };
